import { RemoteImage } from '../components/RemoteImage.js';
import { Section } from '../components/Section.js';
import { type SiteSpeaker, comingSoonCopy, sectionState } from '../lib/site.js';
import { titleFor, useDocumentTitle } from '../lib/useDocumentTitle.js';
import { useSite } from './EventLayout.js';

export function SpeakersPage() {
  const payload = useSite();
  useDocumentTitle(titleFor('Speakers', payload.event.name));

  return (
    <div className="animate-fade-up">
      <h2 className="sr-only">Speakers</h2>
      <Section
        state={sectionState(payload.speakers)}
        comingSoon={comingSoonCopy(payload)}
        emptyTitle="Speakers to be announced"
        emptyBody="The line-up is still being confirmed. Check back and it will be here."
      >
        {(speakers) => (
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {speakers.map((speaker) => (
              <li key={speaker.id}>
                <SpeakerCard speaker={speaker} />
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

/** What stands in for a face: up to two initials, or a dot for a name we cannot split. */
const initials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

function SpeakerCard({ speaker }: { speaker: SiteSpeaker }) {
  const role = [speaker.title, speaker.company].filter(Boolean).join(' · ');

  return (
    <article className="e67-card flex h-full gap-4 px-5 py-5">
      <RemoteImage
        src={speaker.photo ?? speaker.photoUrl}
        // Empty alt, not the speaker's name: the name is the heading right
        // beside it, and repeating it makes a screen reader say it twice.
        alt=""
        className="h-14 w-14 shrink-0 rounded-full object-cover ring-1 ring-brand-hairline"
        fallback={
          <div
            className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-brand-soft text-[15px] font-semibold text-brand-on-surface"
            aria-hidden="true"
          >
            {initials(speaker.name) || '·'}
          </div>
        }
      />
      <div className="min-w-0">
        <h3 className="e67-title text-[16px]">{speaker.name}</h3>
        {role && <p className="mt-0.5 text-[13px] text-brand-muted">{role}</p>}
        {speaker.bio && <p className="e67-prose mt-2 text-[14px] text-pretty">{speaker.bio}</p>}
      </div>
    </article>
  );
}
